import type { AdminUser, MonsterTemplate, MapTemplate, SpellData, LaunchCombatPayload, LaunchCombatResult, CharacterClassData } from '../types/adminTypes';
import type { FrameOverride } from '../utils/spriteFrame';

const ADMIN_TOKEN = import.meta.env.VITE_ADMIN_TOKEN ?? 'darkage-admin-dev-token';
const BASE        = '/api/admin';

// ─────────────────────────────────────────────────────────────────────────────
// Helpers
// ─────────────────────────────────────────────────────────────────────────────

async function req<T>(path: string, options: RequestInit = {}): Promise<T> {
  const url = `${BASE}${path}`;
  // Centralised error logging: every admin-API failure is printed to the
  // console here before being rethrown, so nothing is silently swallowed.
  try {
    const res = await fetch(url, {
      ...options,
      redirect: 'follow',
      headers: {
        'Content-Type': 'application/json',
        'X-Admin-Token': ADMIN_TOKEN,
        ...(options.headers ?? {}),
      },
    });

    // If the request was silently redirected (e.g. 301 POST→GET), expose it
    if (res.redirected && options.method && options.method !== 'GET') {
      throw new Error(
        `Request was redirected from ${url} to ${res.url}. ` +
        `The server may have returned a 301/302 which converted ${options.method} → GET. ` +
        `Clear the Symfony cache: php bin/console cache:clear`
      );
    }

    if (!res.ok) {
      const body = await res.json().catch(() => ({})) as Record<string, unknown>;
      throw new Error((body['error'] as string) ?? `HTTP ${res.status}`);
    }

    // DELETE returns { success: true } — return as-is
    return res.json() as Promise<T>;
  } catch (err) {
    throw err;
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// Monsters
// ─────────────────────────────────────────────────────────────────────────────

export const monsterApi = {
  list:   ()                               => req<MonsterTemplate[]>('/monsters'),
  get:    (id: string)                     => req<MonsterTemplate>(`/monsters/${id}`),
  create: (data: MonsterTemplate)          => req<MonsterTemplate>('/monsters', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<MonsterTemplate>) =>
    req<MonsterTemplate>(`/monsters/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string)                     => req<{ success: boolean }>(`/monsters/${id}`, { method: 'DELETE' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Character Classes
// ─────────────────────────────────────────────────────────────────────────────

export const characterClassApi = {
  list:   ()                               => req<CharacterClassData[]>('/character-classes'),
  get:    (id: string)                     => req<CharacterClassData>(`/character-classes/${id}`),
  create: (data: CharacterClassData)       => req<CharacterClassData>('/character-classes', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<CharacterClassData>) =>
    req<CharacterClassData>(`/character-classes/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string)                     => req<{ success: boolean }>(`/character-classes/${id}`, { method: 'DELETE' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Maps
// ─────────────────────────────────────────────────────────────────────────────

export const mapApi = {
  /** List without the cells array (lighter). */
  list:   ()                               => req<Omit<MapTemplate, 'cells'>[]>('/maps'),
  /** Load full map with cells for editing. */
  get:    (id: string)                     => req<MapTemplate>(`/maps/${id}`),
  create: (data: MapTemplate)              => req<MapTemplate>('/maps', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: MapTemplate)  => req<MapTemplate>(`/maps/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  delete: (id: string)                     => req<{ success: boolean }>(`/maps/${id}`, { method: 'DELETE' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Spells (read-only)
// ─────────────────────────────────────────────────────────────────────────────

export const spellApi = {
  list: () => req<SpellData[]>('/spells'),
  get:  (id: string) => req<SpellData>(`/spells/${id}`),
};

// ─────────────────────────────────────────────────────────────────────────────
// Combat (launch)
// ─────────────────────────────────────────────────────────────────────────────

export const combatApi = {
  launch: (payload: LaunchCombatPayload) =>
    req<LaunchCombatResult>('/combat/launch', { method: 'POST', body: JSON.stringify(payload) }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Users
// ─────────────────────────────────────────────────────────────────────────────

export const userApi = {
  list:    ()            => req<AdminUser[]>('/users'),
  block:   (id: string) => req<{ success: boolean; blocked: boolean }>(`/users/${id}/block`,   { method: 'POST' }),
  unblock: (id: string) => req<{ success: boolean; blocked: boolean }>(`/users/${id}/unblock`, { method: 'POST' }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Ranking Config (admin)
// Consome os endpoints GET/PUT /api/admin/ranking/config
// ─────────────────────────────────────────────────────────────────────────────

export interface RankingPoints {
  win:              number; // Pontos por vitória
  loss:             number; // Penalidade por derrota (negativo)
  abandonPenalty:   number; // Penalidade por abandonar partida (negativo)
  monsterKill:      number; // Pontos base por monstro abatido
  damagePerTen:     number; // Pontos a cada 10 de dano causado
  surviveBonusHp50: number; // Bônus por sobreviver com > 50% HP
  surviveBonusHp80: number; // Bônus por sobreviver com > 80% HP
  mvpBonus:         number; // Bônus para o jogador MVP (maior dano)
}

export interface RankingConfigData {
  displayLimit:   number;  // Máximo de jogadores exibidos no leaderboard
  showAllPlayers: boolean; // Se true, ignora displayLimit e mostra todos
  points:         RankingPoints;
  updatedBy:      string | null;
  updatedAt:      string;
}

export const rankingAdminApi = {
  /** Retorna a configuração global atual do ranking. */
  getConfig:    ()                          => req<RankingConfigData>('/ranking/config'),
  /** Atualiza os pontos e configurações de exibição do ranking. */
  updateConfig: (data: Partial<RankingConfigData>) =>
    req<RankingConfigData>('/ranking/config', { method: 'PUT', body: JSON.stringify(data) }),
};

// ─────────────────────────────────────────────────────────────────────────────
// Game Config (admin)
// ─────────────────────────────────────────────────────────────────────────────

export interface GameConfigData {
  difficultyMultiplier: number;
  monsterXpRate:        number;
  pointsPerLevel:       number;
  xpDecayRate:          number;
  maxLevel:             number;
  levelDelta:           number;
  powerPerExtraPlayer:  number;
  antiCarryMode:        string;
  bossSpawnThreshold:   number;
  maxEnergy:            number;
  energyRechargeRate:   number;
  battleEnergyCost:     number;
  updatedBy:            string | null;
  updatedAt:            string;
}

export const gameConfigAdminApi = {
  getConfig:    () => req<GameConfigData>('/gameconfig'),
  updateConfig: (data: Partial<GameConfigData>) =>
    req<GameConfigData>('/gameconfig', { method: 'PUT', body: JSON.stringify(data) }),
};

// ── Sprite API ─────────────────────────────────────────────────────────────

export interface SpriteEntry {
  id?: string;
  name: string;
  /** Arquivo real servido em /sprites/<filename> (upload = `<name>_<uniqid>.png`). Ausente → usar `<name>.png`. */
  filename?: string;
  label: string;
  icon: string;
  type: 'class' | 'monster' | 'npc';
  cols: number;
  rows: number;
  scale: number;
  offsetX: number;
  offsetY: number;
  /** rowOrder[i] = direção da linha i do sheet. Vazio = ordem padrão. */
  rowOrder?: string[];
  /** Ajuste fino por célula: "r{lin}c{col}" → { scale?, offsetX?, offsetY? }. */
  frameOverrides?: Record<string, FrameOverride>;
}

export const spriteApi = {
  list: (): Promise<SpriteEntry[]> => req<SpriteEntry[]>('/sprites'),

  create: async (formData: FormData): Promise<SpriteEntry> => {
    const res = await fetch(`${BASE}/sprites`, {
      method: 'POST',
      headers: { 'X-Admin-Token': ADMIN_TOKEN },
      body: formData,
    });
    if (!res.ok) {
      if (res.status === 413) {
        throw new Error('Arquivo muito grande. Reduza o tamanho da imagem e tente novamente.');
      }
      // Try to read the error message from the server response body
      let serverMessage = `HTTP ${res.status}`;
      try {
        const body = await res.json();
        serverMessage = body?.error ?? body?.detail ?? body?.message ?? JSON.stringify(body);
      } catch {
        try {
          const text = await res.text();
          // Strip HTML if Symfony debug page returned
          serverMessage = text.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim().slice(0, 400);
        } catch { /* ignore */ }
      }
      throw new Error(serverMessage);
    }
    return res.json();
  },

  delete: async (id: string): Promise<void> => {
    await req(`/sprites/${id}`, { method: 'DELETE' });
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Missions (sem DELETE: desativar com active=false)
// ─────────────────────────────────────────────────────────────────────────────

export interface MissionLevelData {
  map_id: string | null;          // null = aleatório
  monster_level: number;
  monster_count: number | null;   // null = padrão (1–2)
  weather_type: string;           // 'AUTO' = o do mapa
  reward_item_id: string | null;
}

export interface MissionData {
  id?: string;
  name: string;
  active: boolean;
  levels: MissionLevelData[];
  energy_cost: number;
  coin_cost: number;
  hp_mode: 'renew' | 'keep';
  checkpoint_every: number;
  checkpoint_ttl_hours: number;
  drops_enabled: boolean;
  xp_enabled: boolean;
  completion_item_id: string | null;
  keep_rewards_on_loss: boolean;
  defeat_destination: 'hub' | 'dashboard';
  /** Clima afeta o jogo (chuva/neblina/tempestade); false = só visual. */
  weather_effects: boolean;
}

export const missionAdminApi = {
  list:   ()                              => req<MissionData[]>('/missions'),
  get:    (id: string)                    => req<MissionData>(`/missions/${id}`),
  create: (data: MissionData)             => req<MissionData>('/missions', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: MissionData) => req<MissionData>(`/missions/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
};

// ── Locutores do quadro de fim de batalha ────────────────────────────────────

export interface AnnouncerData {
  id?: string;
  name: string;
  side: 'hero' | 'monster';
  image: string;
  active: boolean;
}

export interface AnnouncerPhraseData {
  id?: string;
  kind: 'victory' | 'defeat';
  text_pt: string;
  text_en: string | null;
  active: boolean;
}

export const announcerAdminApi = {
  list:   () => req<{ announcers: AnnouncerData[]; phrases: AnnouncerPhraseData[] }>('/announcers'),
  create: (data: AnnouncerData) => req<AnnouncerData>('/announcers', { method: 'POST', body: JSON.stringify(data) }),
  update: (id: string, data: Partial<AnnouncerData>) => req<AnnouncerData>(`/announcers/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  remove: (id: string) => req<{ success: boolean }>(`/announcers/${id}`, { method: 'DELETE' }),
  uploadImage: async (id: string, file: File): Promise<AnnouncerData> => {
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(`${BASE}/announcers/${id}/image`, { method: 'POST', headers: { 'X-Admin-Token': ADMIN_TOKEN }, body: fd });
    const body = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(body?.error ?? (res.status === 413 ? 'Arquivo muito grande.' : `HTTP ${res.status}`));
    return body as AnnouncerData;
  },
  createPhrase: (data: AnnouncerPhraseData) => req<AnnouncerPhraseData>('/announcer-phrases', { method: 'POST', body: JSON.stringify(data) }),
  updatePhrase: (id: string, data: Partial<AnnouncerPhraseData>) => req<AnnouncerPhraseData>(`/announcer-phrases/${id}`, { method: 'PUT', body: JSON.stringify(data) }),
  removePhrase: (id: string) => req<{ success: boolean }>(`/announcer-phrases/${id}`, { method: 'DELETE' }),
};
